import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Phone, ArrowUpRight } from "lucide-react";
import { useApp } from "@/context/AppContext";

const WHATSAPP = "97440001234";


const sessionKey = "tharwy-help-session";
const getSession = () => {
  let id = sessionStorage.getItem(sessionKey);
  if (!id) {
    id = `help-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    sessionStorage.setItem(sessionKey, id);
  }
  return id;
};

/** A WhatsApp-style help desk: instant AI answers, with a real WhatsApp handover. */
export const HelpChat = () => {
  const { t } = useApp();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ id: "greet", role: "assistant", content: t("hlp_greeting") }]);
  const quick = [t("hlp_q_cost"), t("hlp_q_buy"), t("hlp_q_halal"), t("hlp_q_withdraw")];
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  // The greeting should speak whichever language the visitor just picked.
  useEffect(() => {
    setMessages((m) => (m.length === 1 && m[0].id === "greet"
      ? [{ id: "greet", role: "assistant", content: t("hlp_greeting") }] : m));
  }, [t]);

  // Reopening the page picks the conversation back up where it left off.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/support/chat/${getSession()}`,
          { credentials: "include" });
        if (!res.ok) return;  // 403 means a fresh browser — the greeting is right
        const { messages: prior } = await res.json();
        if (cancelled || !prior?.length) return;
        setMessages((m) => (m.length > 1 ? m : [
          m[0],
          ...prior.map((p, i) => ({ id: `hist-${i}`, role: p.role, content: p.content })),
        ]));
      } catch (e) { console.error("Couldn't load your earlier messages:", e); }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const send = async (text) => {
    const question = (text ?? input).trim();
    if (!question || streaming) return;
    setInput("");
    setStreaming(true);
    const turn = Date.now();
    setMessages((m) => [...m, { id: `u-${turn}`, role: "user", content: question },
                        { id: `a-${turn}`, role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/support/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: question, session_id: getSession() }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        setMessages((m) => {
          const next = [...m];
          const last = next[next.length - 1];
          next[next.length - 1] = { ...last, content: last.content + chunk };
          return next;
        });
      }
    } catch (e) {
      console.error("Help chat failed:", e);
      setMessages((m) => {
        const next = [...m];
        const last = next[next.length - 1];
        next[next.length - 1] = { ...last,
          content: "I couldn't reach our system just then. Please message us on WhatsApp and a "
            + "person will pick it up." };
        return next;
      });
    } finally { setStreaming(false); }
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="help-chat-launcher"
          className="fixed bottom-5 end-5 z-50 flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-3 text-white font-semibold shadow-[0_18px_40px_-12px_rgba(37,211,102,0.7)] hover:brightness-105 active:scale-95 transition-all">
          <MessageCircle className="h-5 w-5" />
          <span className="hidden sm:inline text-sm">{t("hlp_chat_with_us")}</span>
        </button>
      )}

      {open && (
        <div data-testid="help-chat-panel"
          className="fixed bottom-4 end-4 z-50 w-[min(400px,calc(100vw-2rem))] overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/10">
          <div className="flex items-center gap-3 bg-[#075E54] px-4 py-3 text-white">
            <div className="h-9 w-9 rounded-full bg-white/95 flex items-center justify-center p-1"><img src="/tharwy-mark.png" alt="" className="h-full w-full object-contain" /></div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold">{t("hlp_help_desk")}</div>
              <div className="text-[11px] text-white/70">
                {streaming ? t("hlp_maya_typing") : t("hlp_maya_instant")}
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="help-chat-close"
              className="rounded-full p-1 hover:bg-white/10"><X className="h-4 w-4" /></button>
          </div>

          <div className="h-[340px] overflow-y-auto bg-[#ECE5DD] px-3 py-3 space-y-2">
            {messages.map((m, i) => (
              <div key={m.id} className={m.role === "user" ? "flex justify-end" : "flex"}
                data-testid={`help-chat-msg-${m.role}-${i}`}>
                <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap shadow-sm ${
                  m.role === "user" ? "bg-[#DCF8C6] text-[#111B21] rounded-br-md"
                                    : "bg-white text-[#111B21] rounded-bl-md"}`}>
                  {m.content || (streaming ? "…" : "")}
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>

          {messages.length < 3 && (
            <div className="flex gap-1.5 overflow-x-auto bg-[#ECE5DD] px-3 pb-2 no-scrollbar">
              {quick.map((q) => (
                <button key={q} onClick={() => send(q)} data-testid={`help-chat-quick-${q.length}`}
                  className="shrink-0 rounded-full bg-white px-3 py-1.5 text-xs font-medium text-[#075E54] shadow-sm hover:bg-white/80">
                  {q}
                </button>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2 border-t bg-white px-3 py-2.5">
            <input value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()} placeholder={t("hlp_type_message")}
              data-testid="help-chat-input"
              className="flex-1 rounded-full bg-[#F0F2F5] px-3.5 py-2 text-sm outline-none" />
            <button onClick={() => send()} disabled={streaming || !input.trim()}
              data-testid="help-chat-send"
              className="rounded-full bg-[#25D366] p-2 text-white disabled:opacity-40">
              <Send className="h-4 w-4" />
            </button>
          </div>

          <a href={`https://wa.me/${WHATSAPP}`} target="_blank" rel="noreferrer"
            data-testid="help-chat-whatsapp"
            className="flex items-center justify-center gap-1.5 border-t bg-[#F7F8FA] py-2.5 text-xs font-semibold text-[#075E54] hover:bg-[#EFF1F4]">
            <Phone className="h-3.5 w-3.5" />{t("hlp_continue_whatsapp")}
            <ArrowUpRight className="h-3.5 w-3.5" />
          </a>
        </div>
      )}
    </>
  );
};
